/**
 * Story 054 — small fetch helper for the `assets/v1` namespace.
 *
 * Sites + Assets + Components all share the new `/api/assets/v1/*`
 * namespace (locked 2026-05-12 by Christian). Same shape as the
 * Story 052 `authFetch` helper — base URL comes from `getAssetsBaseUrl()`
 * so `DEV_API_MODE` (realbe / devapi-*) routes requests correctly.
 *
 * Errors carry the parsed JSON body so the UI can surface BE error
 * envelopes (`code`, `detail`, `reason`).
 */
import { getAssetsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface AssetsFetchError extends Error {
  status: number;
  body: unknown;
}

interface AssetsFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function assetsFetch<T>(
  path: string,
  opts: AssetsFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    bodyInit = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (bodyInit !== undefined) {
    init.body = bodyInit;
  }
  const res = await fetch(`${getAssetsBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — status still surfaces.
    }
    const err = new Error(
      `assets fetch failed: ${path} → ${String(res.status)}`,
    ) as AssetsFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
